/**
 * Observability Platform — main page component.
 *
 * Auth: Requires login (withAuth wrapper in Page component).
 *
 * Adding a category means adding it to ALARM_CATEGORIES here, to STATUS_CATEGORIES in the BFF,
 * and to getStatusCategories in ATMSMonitoringCDK, which creates its composite alarms.
 */
import React, { useState } from 'react';
import {
  Alert,
  Box,
  Container,
  Header,
  Link,
  Popover,
  SpaceBetween,
  StatusIndicator,
  Toggle,
} from '@amzn/awsui-components-react-v3/polaris';
import { UserRoles, isInternalUser, useSession } from '@amzn/et-console-components';
import I18n from '../../setupI18n';
import { AlarmDetailsTable } from './components/AlarmDetailsTable';
import { HistoryBar } from './components/HistoryBar';
import { SlackIcon } from './components/SlackIcon';
import { useAlarmDetails } from './hooks/useAlarmDetails';
import { useStatus } from './hooks/useStatus';
import { ALARM_CATEGORIES, AlarmCategory, SLACK_BUTTON_STYLES, SLACK_CHANNEL_URL } from './components/constants';
import { AlarmDetail, CategoryStatus, FetchErrorKind } from './types';

function StatusLabel({ status }: { status: CategoryStatus['status'] | null }): JSX.Element {
  switch (status) {
    case 'healthy':
      return <StatusIndicator type="success">{I18n.t('Operational')}</StatusIndicator>;
    case 'degraded':
      return <StatusIndicator type="warning">{I18n.t('Degraded')}</StatusIndicator>;
    case 'outage':
      return <StatusIndicator type="error">{I18n.t('Outage')}</StatusIndicator>;
    default:
      return <StatusIndicator type="stopped">{I18n.t('Unknown')}</StatusIndicator>;
  }
}

function ErrorAlert({ error }: { error: FetchErrorKind }): JSX.Element {
  if (error === 'auth') {
    return (
      <Alert type="warning" header={I18n.t('Unable to load health data')}>
        {I18n.t('Your session may have expired.')}{' '}
        <Link href="/web/login/auth">{I18n.t('Log in again')}</Link>{' '}
        {I18n.t('to view health status.')}
      </Alert>
    );
  }
  return (
    <Alert type="error" header={I18n.t('Unable to load health data')}>
      {I18n.t('Something went wrong.')} <Link href="/web/login/auth">{I18n.t('Log in')}</Link>{' '}
      {I18n.t('or try again later.')}
    </Alert>
  );
}

export function Observability(): JSX.Element {
  const session = useSession();
  const isInternal = 'user' in session && isInternalUser(session);
  const isSysAdmin =
    'user' in session &&
    [UserRoles.SYS_ADMIN, UserRoles.SYS_ADMIN_READ].includes(session.user.role);
  const [showAlarmDetails, setShowAlarmDetails] = useState(false);
  const alarmDetails = useAlarmDetails(isSysAdmin && showAlarmDetails);
  const detailsByCategory = new Map(
    (alarmDetails.data?.categories ?? []).map(({ category, alarms }) => [category, alarms])
  );
  const { data: statusResponse, error, isLoading, lastUpdated } = useStatus();
  const statusById = new Map(
    (statusResponse?.categories ?? []).map(category => [category.id, category])
  );

  return (
    <SpaceBetween size="l">
      <Header
        variant="h1"
        actions={
          isSysAdmin && (
            <Toggle
              checked={showAlarmDetails}
              onChange={({ detail }) => setShowAlarmDetails(detail.checked)}
            >
              {I18n.t('Show alarm details')}
            </Toggle>
          )
        }
      >
        {I18n.t('Observability Platform')}
      </Header>

      <div>
        <Box variant="h3" margin={{ bottom: 'xs' }}>
          {I18n.t('About This Page')}
        </Box>
        <Box color="text-body-secondary">
          {I18n.t(
            'This page shows the health of ATMS over the past 30 days, by what customers use it for. Each bar is one day: green means operational, amber means degraded (working, but slower or with some errors), and red means an outage.'
          )}
        </Box>
      </div>

      {lastUpdated && (
        <Box color="text-body-secondary" fontSize="body-s">
          {I18n.t('Last updated: %{time}', { time: lastUpdated.toLocaleTimeString() })}
          {error && ` ${I18n.t('Refresh failed. Showing the last loaded data.')}`}
        </Box>
      )}

      {!isLoading && error && <ErrorAlert error={error} />}

      {showAlarmDetails && alarmDetails.error && (
        <Alert type="warning" header={I18n.t('Unable to load alarm details')}>
          {I18n.t('Category health above is unaffected. Alarm details retry automatically.')}
        </Alert>
      )}

      <Container
        header={
          <Header
            variant="h2"
            actions={
              isLoading ? (
                <StatusIndicator type="loading">{I18n.t('Loading')}</StatusIndicator>
              ) : (
                <StatusLabel status={statusResponse?.overall.status ?? null} />
              )
            }
          >
            {I18n.t('Overall Health Status')}
          </Header>
        }
      >
        {statusResponse && <HistoryBar history={statusResponse.overall.history} height="40px" />}
      </Container>

      <Header variant="h2" description={I18n.t('Breakdown of service health into categories.')}>
        {I18n.t('Health per Category')}
      </Header>

      {ALARM_CATEGORIES.map(category => (
        <CategoryHealthSection
          key={category.id}
          category={category}
          isLoading={isLoading}
          status={statusById.get(category.id) ?? null}
          alarms={showAlarmDetails ? detailsByCategory.get(category.id) : undefined}
        />
      ))}

      {showAlarmDetails && alarmDetails.data && (
        <Container
          header={
            <Header
              variant="h2"
              description={I18n.t(
                'Alarms firing in this account and region that no category includes. Autoscaling alarms are left out.'
              )}
            >
              {I18n.t('Other firing alarms')}
            </Header>
          }
        >
          <SpaceBetween size="s">
            {alarmDetails.data.otherFiringTruncated && (
              <Box color="text-status-warning">
                {I18n.t('This list may be incomplete: more alarms are firing than the page reads.')}
              </Box>
            )}
            <AlarmDetailsTable alarms={alarmDetails.data.otherFiring} />
          </SpaceBetween>
        </Container>
      )}

      {isInternal && (
        <Container
          header={
            <Header
              variant="h2"
              actions={
                <a
                  href={SLACK_CHANNEL_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={SLACK_BUTTON_STYLES}
                >
                  <SlackIcon size={16} />
                  {I18n.t('Subscribe via Slack')}
                </a>
              }
            >
              {I18n.t('Get Proactive Alerts in Slack')}
            </Header>
          }
        >
          <Box color="text-body-secondary">
            {I18n.t(
              'The ATMS bot posts when degradation is detected and when services recover.'
            )}
          </Box>
        </Container>
      )}
    </SpaceBetween>
  );
}

/**
 * One category's status and history. `status` is null before the first load; a category the
 * BFF could not read comes back with status 'unknown'.
 */
function CategoryHealthSection({
  category,
  isLoading,
  status,
  alarms,
}: {
  category: AlarmCategory;
  isLoading: boolean;
  status: CategoryStatus | null;
  alarms?: AlarmDetail[];
}): JSX.Element {
  return (
    <Container
      header={
        <Header
          variant="h3"
          info={
            <Popover
              header={category.label}
              content={category.description}
              triggerType="custom"
              size="medium"
            >
              <Link variant="info">{I18n.t('Info')}</Link>
            </Popover>
          }
          actions={
            isLoading ? (
              <StatusIndicator type="loading">{I18n.t('Loading')}</StatusIndicator>
            ) : (
              <StatusLabel status={status?.status ?? null} />
            )
          }
        >
          {category.label}
        </Header>
      }
    >
      {status?.status === 'unknown' && (
        <Box color="text-status-error">{I18n.t('Unable to load data for this category.')}</Box>
      )}
      {status && status.status !== 'unknown' && <HistoryBar history={status.history} height="24px" />}
      {alarms && <AlarmDetailsTable alarms={alarms} />}
    </Container>
  );
}
